import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGamerAuth } from './GamerAuthContext';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

export default function GamerProfile() {
    const { gamerUser, logoutGamer, loading } = useGamerAuth();
    const [vaultTab, setVaultTab] = useState('BOUGHT');

    useEffect(() => {
        const verifyUserExists = async () => {
            if (!loading && gamerUser) {
                try {
                    const userId = gamerUser.uid || gamerUser.gamerCode;
                    if (userId) {
                        const docRef = gamerUser.uid ? doc(db, 'users', userId) : doc(db, 'gameswap', 'data', 'gamers', userId);
                        const docSnap = await getDoc(docRef);
                        if (!docSnap.exists()) {
                            if (logoutGamer) logoutGamer();
                            window.location.hash = '#';
                            alert("GAMER NOT EXISTS");
                        }
                    }
                } catch (error) {
                    console.error("Error verifying user existence:", error);
                }
            }
        };
        verifyUserExists();
    }, [gamerUser, loading, logoutGamer]);

    if (!gamerUser) {
        return (
            <section className="relative w-full min-h-screen pt-24 pb-20 px-4 sm:px-8 bg-[#050505] font-mono flex items-center justify-center">
                <div className="text-center">
                    <div className="text-6xl mb-6">🔒</div>
                    <h2 className="text-2xl font-black text-white tracking-widest mb-3">NOT LOGGED IN</h2>
                    <p className="text-gray-500 text-sm tracking-wider mb-8">Please login with your GAMER CODE to view your profile.</p>
                    <a href="#" onClick={() => window.location.hash = ''} className="inline-flex items-center gap-2 px-6 py-3 bg-pink-600 hover:bg-pink-500 text-white text-sm font-black tracking-widest rounded-xl transition-colors">
                        ← BACK TO STORE
                    </a>
                </div>
            </section>
        );
    }

    const gamerCode = gamerUser.gamerCode || '—';
    const displayName = gamerUser.profileData?.name || `Gamer_${gamerCode.substring(0, 4)}`;
    const joinedAt = gamerUser.profileData?.joinedAt
        ? new Date(gamerUser.profileData.joinedAt).getFullYear()
        : '—';
    const unlockedGames = gamerUser.unlockedGames || [];
    const gamesOwned = unlockedGames.length;

    const handleLogout = () => {
        logoutGamer();
        window.location.hash = '';
    };

    return (
        <section className="relative w-full min-h-screen pt-24 pb-20 px-4 sm:px-8 bg-[#050505] font-mono overflow-hidden">
            {/* Ambient Background */}
            <div className="absolute inset-0 pointer-events-none opacity-20" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
            <div className="absolute top-0 left-1/4 w-[50vw] h-[50vw] bg-pink-500/10 blur-[150px] rounded-full pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-[50vw] h-[50vw] bg-purple-500/10 blur-[150px] rounded-full pointer-events-none" />

            <div className="max-w-5xl mx-auto relative z-10">
                {/* Back Link */}
                <div className="mb-6">
                    <a href="#" onClick={(e) => { e.preventDefault(); window.location.hash = ''; }} className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm uppercase tracking-widest font-bold">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                        Back to Store
                    </a>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="w-full"
                >
                    {/* Header Bar */}
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between py-8 border-b border-white/10 gap-6">
                        <div className="flex items-center gap-5">
                            {/* Avatar - initials only */}
                            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 p-[3px] shadow-[0_0_30px_rgba(236,72,153,0.3)]">
                                <div className="w-full h-full rounded-full bg-[#0a0a0a] flex items-center justify-center">
                                    <span className="text-white font-black text-2xl tracking-tighter">
                                        {gamerCode.substring(0, 2).toUpperCase()}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <h2 className="text-2xl sm:text-3xl font-black text-white tracking-widest uppercase mb-2">
                                    {displayName.split('_')[0]}<span className="text-pink-500">{displayName.includes('_') ? '_' + displayName.split('_').slice(1).join('_') : ''}</span>
                                </h2>
                                <div className="flex flex-wrap items-center gap-3">
                                    <span className="text-sm text-gray-400 tracking-wider flex items-center gap-2">
                                        <svg className="w-4 h-4 text-pink-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
                                        GAMER CODE: <span className="text-white font-black select-all bg-white/5 px-2 py-0.5 rounded tracking-widest">{gamerCode}</span>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 px-5 py-2.5 bg-white/5 hover:bg-red-500/10 border border-white/10 hover:border-red-500/40 rounded-xl text-sm font-bold text-gray-400 hover:text-red-400 tracking-widest transition-all duration-300"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                            LOGOUT
                        </button>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-8 mb-10">
                        {[
                            { label: 'GAMES OWNED', value: gamesOwned, icon: 'M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z' },
                            { label: 'MEMBER SINCE', value: joinedAt, icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
                            { label: 'VAULT STATUS', value: gamesOwned > 0 ? 'ACTIVE' : 'EMPTY', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
                        ].map((stat, i) => (
                            <div key={i} className="bg-[#111] border border-white/5 p-5 rounded-2xl flex flex-col items-center justify-center text-center group hover:border-pink-500/30 transition-colors relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-3 opacity-10 text-white group-hover:text-pink-500 transition-colors">
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={stat.icon} /></svg>
                                </div>
                                <span className="text-[10px] sm:text-xs text-gray-500 tracking-[0.15em] mb-2 font-bold z-10">{stat.label}</span>
                                <span className="text-xl sm:text-2xl lg:text-3xl font-black text-white group-hover:text-pink-400 transition-colors z-10">{stat.value}</span>
                            </div>
                        ))}
                    </div>

                    {/* THE VAULT */}
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl sm:text-2xl font-black text-white tracking-widest flex items-center gap-4">
                            <span className="relative flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-pink-500"></span>
                            </span>
                            THE VAULT
                        </h3>
                        <span className="text-xs text-gray-500 tracking-widest font-bold">{gamesOwned} ITEM{gamesOwned !== 1 ? 'S' : ''}</span>
                    </div>

                    {/* Games List */}
                    <div className="flex flex-col gap-3">
                        <AnimatePresence>
                            {unlockedGames.length === 0 ? (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="bg-[#111] border border-white/5 rounded-2xl p-12 text-center"
                                >
                                    <div className="text-5xl mb-4">🎮</div>
                                    <p className="text-gray-500 text-sm tracking-widest font-bold uppercase">No games unlocked yet</p>
                                    <p className="text-gray-600 text-xs tracking-wider mt-2">Buy a game in the store to unlock it here.</p>
                                </motion.div>
                            ) : (
                                unlockedGames.map((gameId, index) => (
                                    <motion.div
                                        key={gameId}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        className="group flex items-center justify-between bg-[#111] border border-white/5 hover:border-pink-500/30 px-5 py-4 rounded-xl transition-all duration-300 relative overflow-hidden"
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-r from-pink-500/0 via-pink-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                                        <div className="flex items-center gap-4 relative z-10 min-w-0">
                                            {/* Index number instead of image */}
                                            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-pink-600/20 to-purple-600/20 border border-pink-500/20 flex items-center justify-center shrink-0">
                                                <span className="text-pink-400 font-black text-sm">{index + 1}</span>
                                            </div>
                                            <span className="text-white font-bold tracking-wide text-sm sm:text-base truncate">{gameId}</span>
                                        </div>

                                        <div className="relative z-10 shrink-0 ml-4">
                                            <span className="px-3 py-1 bg-green-500/10 border border-green-500/20 text-green-400 text-[10px] font-black tracking-widest rounded-lg uppercase">
                                                UNLOCKED
                                            </span>
                                        </div>
                                    </motion.div>
                                ))
                            )}
                        </AnimatePresence>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
